import React, { useState } from 'react';
import { CopyX, FileText, Table } from 'lucide-react';
import { toast } from 'sonner';
import {
  exportDuplicateEmailsCSV,
  exportDuplicateEmailsExcel,
  listDuplicateEmails,
} from '../../utils/api';
import { useT } from '../../i18n';
import EmailPanelDialog from './EmailPanelDialog';
import { DuplicateGroups } from './DuplicatePlan';

/**
 * Le second compte de la même opération, nommé à son tour.
 *
 * « 4 doublons retirés » ne se vérifie pas davantage que « 12 invalides » —
 * c'est le même défaut, une case à cocher plus loin. La fenêtre montre la
 * ligne gardée à côté de celles qui partent, avec le reste de la ligne : c'est
 * ce qui distingue deux doublons l'un de l'autre.
 *
 * Les doublons se décident **après** les autres étapes : une ligne déjà
 * retirée comme invalide n'est le doublon de rien. Le panneau envoie donc tout
 * le formulaire et le serveur rejoue le nettoyage jusqu'à cette étape, plutôt
 * que de reproduire l'ordre de son côté.
 */
const DuplicateEmailPanel = ({ column, normalize, removeInvalid, removeValues }) => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  const config = {
    column,
    normalize,
    remove_invalid: Boolean(removeInvalid),
    remove_values: removeValues || [],
  };

  const show = async () => {
    setOpen(true);
    setLoading(true);
    setPlan(null);
    try {
      const result = await listDuplicateEmails(config);
      setPlan(result.report);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('email.failed'));
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="card p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium">{t('email.duplicatesOpen')}</p>
            <p className="text-xs text-muted-foreground">{t('email.duplicatesHelp')}</p>
          </div>
          <button
            type="button"
            onClick={show}
            disabled={!column || loading}
            className="btn-secondary whitespace-nowrap flex items-center gap-1.5"
            data-testid="email-duplicates-open"
          >
            <CopyX className="h-4 w-4" />
            {t('email.duplicatesOpen')}
          </button>
        </div>
      </div>

      {open && (
        <EmailPanelDialog
          title={t('email.duplicatesTitle', { column })}
          testId="email-duplicates"
          onClose={() => setOpen(false)}
        >
          {loading && (
            <p className="text-sm text-muted-foreground" data-testid="email-duplicates-loading">
              {t('email.panelLoading')}
            </p>
          )}

          {plan && (
            <>
              <p className="text-sm font-medium" data-testid="email-duplicates-summary">
                {plan.rows_removed === 0
                  ? t('duplicates.planNothing')
                  : t('duplicates.planSummary', {
                      rows: plan.rows_removed,
                      total: plan.rows_total,
                      groups: plan.groups_total,
                    })}
              </p>
              <p className="text-xs text-muted-foreground">
                {normalize ? t('email.panelNormalized') : t('email.panelRaw')}
              </p>
              {removeInvalid && (
                <p className="text-xs text-muted-foreground" data-testid="email-duplicates-after-invalid">
                  {t('email.duplicatesAfterInvalid')}
                </p>
              )}

              {plan.rows_without_key > 0 && (
                <p className="text-xs text-muted-foreground" data-testid="email-duplicates-without-key">
                  {t('duplicates.planWithoutKey', { rows: plan.rows_without_key })}
                </p>
              )}

              <DuplicateGroups plan={plan} className="max-h-72 overflow-y-auto space-y-3" />

              {plan.rows_removed > 0 && (
                <div className="border-t border-border pt-4 space-y-2">
                  <p className="text-xs text-muted-foreground">{t('email.panelExportNote')}</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => exportDuplicateEmailsCSV(config)}
                      className="btn-primary flex items-center gap-2"
                      data-testid="email-duplicates-export-csv"
                    >
                      <FileText className="h-4 w-4" />
                      {t('export.csv')}
                    </button>
                    <button
                      type="button"
                      onClick={() => exportDuplicateEmailsExcel(config)}
                      className="btn-primary flex items-center gap-2"
                      data-testid="email-duplicates-export-excel"
                    >
                      <Table className="h-4 w-4" />
                      {t('export.excel')}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </EmailPanelDialog>
      )}
    </>
  );
};

export default DuplicateEmailPanel;
